import * as React4 from 'react';
import React4__default, { useRef, useEffect, useMemo, Children, useCallback, useLayoutEffect, useReducer, useState } from 'react';
import PubSub from 'pubsub-js';
import { useSyncExternalStore } from 'use-sync-external-store/shim';
import { jwtDecode } from 'jwt-decode';

// src/useCallbackRef/useCallbackRef.ts
function useCallbackRef(callback) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });
  return useMemo(() => (...args) => {
    var _a;
    return (_a = callbackRef.current) == null ? void 0 : _a.call(callbackRef, ...args);
  }, []);
}
function useChildrenValidation(children, componentName, AllowedChildren) {
  useMemo(() => {
    Children.forEach(children, (child) => {
      const includesType = Array.isArray(AllowedChildren) ? AllowedChildren.includes(child.type) || AllowedChildren.includes(child.type.target) : child.type === AllowedChildren || child.type.target === AllowedChildren;
      if (!includesType) {
        throw new Error(
          `${componentName} only allows ${Array.isArray(AllowedChildren) ? AllowedChildren.map((item) => item.displayName).join(", ") : AllowedChildren.displayName} as immediate children. Found invalid child ${child.type.displayName}.`
        );
      }
    });
  }, [children, componentName, AllowedChildren]);
}
var useCloseOnClick = (options) => {
  const { container, node, triggerNode, closeOnInsideClick, onClose } = options;
  const handleClick = useCallback(
    (event) => {
      const target = event.target;
      if (closeOnInsideClick) {
        setTimeout(onClose, 0);
        return;
      }
      const hasClickedOnTheTrigger = triggerNode.contains(target);
      const hasClickedOnThePortal = node.contains(target);
      if (!hasClickedOnTheTrigger && !hasClickedOnThePortal) {
        onClose();
      }
    },
    [triggerNode, node, closeOnInsideClick, onClose]
  );
  useEffect(() => {
    if (!node || !triggerNode) {
      return;
    }
    container.addEventListener("click", handleClick, true);
    return () => {
      container.removeEventListener("click", handleClick, true);
    };
  }, [container, handleClick, node, triggerNode]);
};
var useCloseOnEscape = (show, handler) => {
  const onCloseHandler = useCallbackRef(handler);
  React4__default.useEffect(() => {
    const onEscape = (e) => {
      if (show && e.key === "Escape") {
        onCloseHandler();
      }
    };
    document.addEventListener("keyup", onEscape, true);
    return () => {
      document.removeEventListener("keyup", onEscape, true);
    };
  }, [show, onCloseHandler]);
};
var useCloseOnResize = (options) => {
  const { node, onClose } = options;
  useEffect(() => {
    if (!node) {
      return;
    }
    window.addEventListener("resize", onClose, false);
    return () => {
      window.removeEventListener("resize", onClose, false);
    };
  }, [node, onClose]);
};
var useCloseOnScroll = (options) => {
  const { container, node, onClose } = options;
  const handleScroll = useCallback(
    (event) => {
      const target = event.target;
      const hasScrolledInsideOfPortal = node.contains(target);
      if (!hasScrolledInsideOfPortal) {
        onClose();
      }
    },
    [node, onClose]
  );
  useEffect(() => {
    if (!node) {
      return;
    }
    container.addEventListener("scroll", handleScroll, true);
    return () => {
      container.removeEventListener("scroll", handleScroll, true);
    };
  }, [container, handleScroll, node]);
};
function setRef(ref, value) {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref !== null && ref !== void 0) {
    ref.current = value;
  }
}
function composeRefs(...refs) {
  return (node) => refs.forEach((ref) => setRef(ref, node));
}
function useComposedRefs(...refs) {
  return React4.useCallback(composeRefs(...refs), refs);
}

// src/misc/util.ts
var isBrowser = typeof window !== "undefined";
var noop = (...args) => {
};

// src/useControllableState/useControllableState.ts
function useControllableState({
  prop,
  defaultProp,
  onChange = noop
}) {
  const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({ defaultProp, onChange });
  const isControlled = prop !== void 0;
  const value = isControlled ? prop : uncontrolledProp;
  const handleChange = useCallbackRef(onChange);
  const setValue = React4.useCallback(
    (nextValue) => {
      if (isControlled) {
        const setter = nextValue;
        const settedValue = typeof nextValue === "function" ? setter(prop) : nextValue;
        if (settedValue !== prop)
          handleChange(settedValue);
      } else {
        setUncontrolledProp(nextValue);
      }
    },
    [isControlled, prop, setUncontrolledProp, handleChange]
  );
  return [value, setValue];
}
function useUncontrolledState({
  defaultProp,
  onChange
}) {
  const uncontrolledState = React4.useState(defaultProp);
  const [value] = uncontrolledState;
  const prevValueRef = React4.useRef(value);
  const handleChange = useCallbackRef(onChange);
  React4.useEffect(() => {
    if (prevValueRef.current !== value) {
      handleChange(value);
      prevValueRef.current = value;
    }
  }, [value, prevValueRef, handleChange]);
  return uncontrolledState;
}
var useEffectOnce = (effect) => {
  useEffect(effect, []);
};
var globalId = 0;
function useShimId() {
  const [defaultId, setDefaultId] = React4.useState();
  React4.useEffect(() => {
    if (typeof defaultId !== "undefined") {
      setDefaultId(`tractor-${++globalId}`);
    }
  }, [defaultId]);
  return defaultId;
}
var ReactUseId = "useId";
var maybeReactUseId = React4[ReactUseId];
function useId() {
  if (maybeReactUseId !== void 0) {
    const reactId = maybeReactUseId();
    return reactId;
  }
  return useShimId();
}
var useIsMounted = () => {
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return useCallback(() => isMounted.current, []);
};
function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
var useSafeLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

// src/useMount/useMount.ts
var useMount = (fn) => {
  useEffectOnce(() => {
    fn();
  });
};
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}
var usePubSub = (topic, cb) => {
  const cbRef = useLatest(cb);
  useEffect(() => {
    const token = PubSub.subscribe(topic, (_, data) => {
      cbRef.current(data);
    });
    return () => {
      PubSub.unsubscribe(token);
    };
  }, [topic, cbRef]);
};

// src/useRect/utils.ts
function observeElementRect(elementToObserve, callback) {
  const observedData = observedElements.get(elementToObserve);
  if (observedData === void 0) {
    observedElements.set(elementToObserve, { rect: {}, callbacks: [callback] });
    if (observedElements.size === 1) {
      rafId = requestAnimationFrame(runLoop);
    }
  } else {
    observedData.callbacks.push(callback);
    callback(elementToObserve.getBoundingClientRect());
  }
  return () => {
    const observedData2 = observedElements.get(elementToObserve);
    if (observedData2 === void 0)
      return;
    const index = observedData2.callbacks.indexOf(callback);
    if (index > -1) {
      observedData2.callbacks.splice(index, 1);
    }
    if (observedData2.callbacks.length === 0) {
      observedElements.delete(elementToObserve);
      if (observedElements.size === 0) {
        cancelAnimationFrame(rafId);
      }
    }
  };
}
var rafId;
var observedElements = /* @__PURE__ */ new Map();
function runLoop() {
  const changedRectsData = [];
  observedElements.forEach((data, element) => {
    const newRect = element.getBoundingClientRect();
    if (!rectEquals(data.rect, newRect)) {
      data.rect = newRect;
      changedRectsData.push(data);
    }
  });
  changedRectsData.forEach((data) => {
    data.callbacks.forEach((callback) => callback(data.rect));
  });
  rafId = requestAnimationFrame(runLoop);
}
function rectEquals(rect1, rect2) {
  return rect1.width === rect2.width && rect1.height === rect2.height && rect1.top === rect2.top && rect1.right === rect2.right && rect1.bottom === rect2.bottom && rect1.left === rect2.left;
}

// src/useRect/useRect.ts
function useRect(measurable) {
  const [rect, setRect] = React4.useState();
  React4.useEffect(() => {
    if (!measurable) {
      return;
    }
    const unobserve = observeElementRect(measurable, setRect);
    return () => {
      setRect(void 0);
      unobserve();
    };
  }, [measurable]);
  return rect;
}
function useSize(element) {
  const [size, setSize] = React4.useState(void 0);
  React4.useEffect(() => {
    if (!element) {
      setSize(void 0);
      return;
    }
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries.length) {
        return;
      }
      const entry = entries[0];
      let width;
      let height;
      if ("borderBoxSize" in entry) {
        const borderSizeEntry = entry["borderBoxSize"];
        const borderSize = Array.isArray(borderSizeEntry) ? borderSizeEntry[0] : borderSizeEntry;
        width = borderSize["inlineSize"];
        height = borderSize["blockSize"];
      } else {
        const rect = element.getBoundingClientRect();
        width = rect.width;
        height = rect.height;
      }
      requestAnimationFrame(() => setSize({ width, height }));
    });
    resizeObserver.observe(element, { box: "border-box" });
    return () => resizeObserver.unobserve(element);
  }, [element]);
  return size;
}
var useToggle = (initialValue) => {
  return useReducer(
    (state, nextValue) => typeof nextValue === "boolean" ? nextValue : !state,
    initialValue
  );
};
var getPermissions = (token) => {
  if (!token) {
    return;
  }
  try {
    const decodedToken = jwtDecode(token);
    const permissionsString = decodedToken["custom:permissions"];
    return JSON.parse(permissionsString);
  } catch (e) {
    return;
  }
};

// src/useTokenPermissions/useTokenPermissions.ts
var useTokenPermissions = (subscribe, getSnapshot) => {
  const token = useSyncExternalStore(subscribe, getSnapshot);
  return getPermissions(token);
};

// src/useUnmount/useUnmount.ts
var useUnmount = (fn) => {
  const fnRef = useCallbackRef(fn);
  useEffectOnce(() => () => fnRef());
};
var WINDOW_RESIZE_DEBOUNCE_WAIT_IN_MS = 100;
function useWindowSize() {
  const [windowSize, setWindowSize] = React4.useState(
    void 0
  );
  React4.useEffect(() => {
    let debounceTimerId;
    function updateWindowSize() {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    }
    function handleResize() {
      window.clearTimeout(debounceTimerId);
      debounceTimerId = window.setTimeout(updateWindowSize, WINDOW_RESIZE_DEBOUNCE_WAIT_IN_MS);
    }
    updateWindowSize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowSize;
}
var useKeyDown = ({
  code,
  withShiftKey,
  withCtrlKey,
  withCmdKey,
  withAltKey,
  options = { enabled: true },
  action
}) => {
  const execFunction = useCallback(
    (event) => {
      if (event.code === code && (withShiftKey ? event.shiftKey : true) && (withCtrlKey ? event.ctrlKey : true) && (withCmdKey ? event.metaKey : true) && (withAltKey ? event.altKey : true)) {
        action(event);
      }
    },
    [action, code, withShiftKey, withCtrlKey, withCmdKey, withAltKey]
  );
  useEffect(() => {
    if (options.enabled) {
      document.addEventListener("keydown", execFunction, false);
    }
    return () => {
      document.removeEventListener("keydown", execFunction, false);
    };
  }, [execFunction, options]);
};
function useHasScrollableContent() {
  const scrollableContainerRef = useRef(null);
  const [hasScrollableContentBottom, setHasScrollableContentBottom] = useState(false);
  const updateScrollableContentBottom = useCallback((element) => {
    const { clientHeight, scrollHeight, scrollTop } = element;
    const isAtTheTop = scrollTop === 0;
    const isInBetween = scrollTop > 0 && clientHeight < scrollHeight - scrollTop;
    const isAtTheBottom = clientHeight === scrollHeight - scrollTop;
    setHasScrollableContentBottom((isAtTheTop || isInBetween) && !isAtTheBottom);
  }, []);
  const onScrollHandler = useCallback(
    (event) => {
      updateScrollableContentBottom(event.currentTarget);
    },
    [updateScrollableContentBottom]
  );
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        updateScrollableContentBottom(entry.target);
      });
    });
    if (scrollableContainerRef.current) {
      resizeObserver.observe(scrollableContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [updateScrollableContentBottom]);
  const useHasScrollableContentReturn = useMemo(
    () => ({
      hasScrollableContentBottom,
      scrollableContainerRef,
      onScrollHandler
    }),
    [hasScrollableContentBottom, onScrollHandler]
  );
  return useHasScrollableContentReturn;
}

export { composeRefs, useCallbackRef, useChildrenValidation, useCloseOnClick, useCloseOnEscape, useCloseOnResize, useCloseOnScroll, useComposedRefs, useControllableState, useEffectOnce, useHasScrollableContent, useId, useIsMounted, useKeyDown, useLatest, useMount, usePrevious, usePubSub, useRect, useSafeLayoutEffect, useSize, useToggle, useTokenPermissions, useUnmount, useWindowSize };
